/**
 * @file 触发符查询提取 / 文本替换工具函数（Mention 共享层）
 * Skill `/`、Knowledge `@`、Chart `#` 共用
 */
import { mentionLabelMatchVariants } from '../utils/inlineMentionLabel';

/**
 * 提取触发符后、到首个空白前的 query 文本
 * @param value - 完整输入值
 * @param triggerIndex - 触发符所在索引
 * @returns 触发符后的查询字符串
 */
export function extractMentionQuery(value: string, triggerIndex: number): string {
  const after = value.substring(triggerIndex + 1);
  const match = after.match(/^([^\s]*)/);
  return match ? match[1] : '';
}

/**
 * 计算触发符 + query 的删除终点；不超过光标，避免误删光标后的正文
 */
export function computeMentionTriggerRemoveEnd(
  value: string,
  triggerIndex: number,
  selectionStart?: number,
): number {
  const query = extractMentionQuery(value, triggerIndex);
  const rawRemoveEnd = triggerIndex + 1 + query.length;
  if (selectionStart == null) return rawRemoveEnd;
  return Math.min(
    rawRemoveEnd,
    Math.max(triggerIndex + 1, selectionStart),
  );
}

/**
 * 解析触发符索引：优先使用 hint，不匹配时从光标向前回溯
 */
export function resolveMentionTriggerIndex(
  value: string,
  triggerChar: string,
  triggerIndexHint: number,
  selectionStart: number,
): number {
  if (
    triggerIndexHint >= 0
    && triggerIndexHint < value.length
    && value.charAt(triggerIndexHint) === triggerChar
  ) {
    return triggerIndexHint;
  }
  const searchFrom = Math.min(selectionStart, value.length);
  for (let i = searchFrom;i >= 0;i -= 1) {
    if (value.charAt(i) === triggerChar) return i;
  }
  return triggerIndexHint;
}

/** 判断 mention 正文是否含 CJK（用于 skill tag 间距） */
export function detectMentionScript(text: string): 'cjk' | 'latin' {
  return /[\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff]/.test(text) ? 'cjk' : 'latin';
}

/**
 * 删除指定索引处的单个字符（触发符清除用）
 * @param value - 完整输入值
 * @param index - 要删除的字符索引
 * @returns 删除后的新字符串
 */
export function removeCharAt(value: string, index: number): string {
  return value.substring(0, index) + value.substring(index + 1);
}

/**
 * 删除触发符 + query，并在该位置插入文案；
 * 无触发符时在 selectionStart~selectionEnd 处插入（按钮模式选中）。
 * @param value - 完整输入值
 * @param insertText - 要插入的文案
 * @param options - 替换选项
 * @param options.triggerIndex - 触发符索引（@ 模式）
 * @param options.selectionStart - 选区起始（按钮模式）
 * @param options.selectionEnd - 选区结束（按钮模式）
 * @returns 替换后的字符串和光标位置
 */
export function replaceTriggerOrInsertAt(
  value: string,
  insertText: string,
  options: {
    triggerIndex?: number;
    selectionStart?: number;
    selectionEnd?: number;
  } = {},
): { nextValue: string; cursor: number; } {
  let insert = insertText || '';
  const {
    triggerIndex = -1,
    selectionStart = value.length,
    selectionEnd = selectionStart,
  } = options;

  if (triggerIndex >= 0 && triggerIndex < value.length) {
    const removeEnd = computeMentionTriggerRemoveEnd(
      value,
      triggerIndex,
      selectionStart,
    );
    // 与前一字符之间补空格，避免引用串贴紧正文
    if (
      insert
      && triggerIndex > 0
      && !/\s/.test(value[triggerIndex - 1])
      && !/^\s/.test(insert)
    ) {
      insert = ` ${insert}`;
    }
    const nextValue =
      value.substring(0, triggerIndex) + insert + value.substring(removeEnd);
    return { nextValue, cursor: triggerIndex + insert.length };
  }

  const start = Math.max(0, selectionStart);
  const end = Math.max(start, selectionEnd);
  if (
    insert
    && start > 0
    && !/\s/.test(value[start - 1])
    && !/^\s/.test(insert)
  ) {
    insert = ` ${insert}`;
  }
  // 插入后若紧贴后文且自身无尾空格，补一个空格
  if (
    insert
    && end < value.length
    && !/\s/.test(value[end])
    && !/\s$/.test(insert)
  ) {
    insert = `${insert} `;
  }
  const nextValue = value.substring(0, start) + insert + value.substring(end);
  return { nextValue, cursor: start + insert.length };
}

/**
 * 从正文中移除一段内联引用 label，尽量连同相邻空格一并清理
 * @param value - 完整输入值
 * @param label - 要移除的 label 文本
 * @returns 清理后的字符串
 */
export function removeInlineLabelFromValue(value: string, label: string): string {
  const trimmed = label.trim();
  if (!trimmed) return value;

  const variants = mentionLabelMatchVariants(trimmed);
  for (const variant of variants) {
    if (!value.includes(variant)) continue;
    const patterns = [` ${variant} `, `${variant} `, ` ${variant}`, variant];
    for (const pattern of patterns) {
      if (!value.includes(pattern)) continue;
      const next = value.replace(pattern, pattern === variant ? '' : ' ');
      return next.replace(/\s{2,}/g, ' ').trimEnd();
    }
  }
  return value;
}
